import type { RefObject } from "react";

import {
  useEventListener as useEventListenerLib,
  useHover as useHoverLib,
  useOnClickOutside as useOnClickOutsideLib,
} from "usehooks-ts";

/**
 * Thin wrappers around the `usehooks-ts` hooks that take an element ref.
 *
 * `usehooks-ts@3` still types ref parameters as the non-nullable `RefObject<T>`,
 * but under React 19 `useRef<T>(null)` yields `RefObject<T | null>`. Without these
 * wrappers every call site has to write `ref as RefObject<T>` to bridge the gap.
 * The wrappers accept the null-tolerant ref type and perform that cast once — the
 * hooks already handle a null `current` at runtime, so behaviour is unchanged.
 *
 * Import these instead of the `usehooks-ts` originals whenever you pass a ref.
 */

export const useHover = <T extends HTMLElement = HTMLElement>(
  elementRef: RefObject<T | null>,
): boolean => useHoverLib(elementRef as RefObject<T>);

type ClickOutsideEventType = NonNullable<
  Parameters<typeof useOnClickOutsideLib>[2]
>;

export const useOnClickOutside = <T extends HTMLElement = HTMLElement>(
  ref: RefObject<T | null> | RefObject<T | null>[],
  handler: (event: MouseEvent | TouchEvent | FocusEvent) => void,
  eventType?: ClickOutsideEventType,
  eventListenerOptions?: AddEventListenerOptions,
): void =>
  useOnClickOutsideLib(
    ref as RefObject<T> | RefObject<T>[],
    handler,
    eventType,
    eventListenerOptions,
  );

/**
 * `useEventListener` is overloaded four ways (media-query / window / element /
 * document); only the ref-bearing overloads need widening. Rather than
 * re-implement every overload, we re-expose the hook with the ref parameters
 * relaxed to allow `null`. The runtime implementation is untouched.
 */
type UseEventListener = {
  <K extends keyof MediaQueryListEventMap>(
    eventName: K,
    handler: (event: MediaQueryListEventMap[K]) => void,
    element: RefObject<MediaQueryList | null>,
    options?: boolean | AddEventListenerOptions,
  ): void;
  <K extends keyof WindowEventMap>(
    eventName: K,
    handler: (event: WindowEventMap[K]) => void,
    element?: undefined,
    options?: boolean | AddEventListenerOptions,
  ): void;
  <
    K extends keyof HTMLElementEventMap & keyof SVGElementEventMap,
    T extends Element = K extends keyof HTMLElementEventMap
      ? HTMLDivElement
      : SVGElement,
  >(
    eventName: K,
    handler:
      | ((event: HTMLElementEventMap[K]) => void)
      | ((event: SVGElementEventMap[K]) => void),
    element: RefObject<T | null>,
    options?: boolean | AddEventListenerOptions,
  ): void;
  <K extends keyof DocumentEventMap>(
    eventName: K,
    handler: (event: DocumentEventMap[K]) => void,
    element: RefObject<Document | null>,
    options?: boolean | AddEventListenerOptions,
  ): void;
};

export const useEventListener =
  useEventListenerLib as unknown as UseEventListener;
